import React from 'react'
import { Button, Col, Pagination, Row, Space, Typography, ConfigProvider, Menu } from 'antd'
import { Plus, Filter } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { useDashboardContext, ITEMS_PER_PAGE } from '../context/dashboard'
import { useAuthContext } from '../context/auth';
import { SortState, SortType } from '../../types/sort'
import { SecondaryButton } from '../components/ui/secondary-button'

import DashboardNavbar from '../components/dashboard/navbar'
import MetricsContent from '../components/dashboard/metrics/metrics-container'

const { Title } = Typography

interface DashboardPageProps {
  onNavigateToContent: () => void
}

export const SortButton = () => {
  const { sortConfig, setSortConfig } = useDashboardContext()
  const [menuVisible, setMenuVisible] = React.useState(false)
  const menuContainerRef = React.useRef<HTMLDivElement>(null)

  // Cerrar el menú cuando se hace clic fuera de él
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(event.target as Node)) {
        setMenuVisible(false)
      }
    }

    if (menuVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuVisible])

  const handleMenuClick = (type: SortType) => {
    setSortConfig((prev: SortState) => ({
      type,
      direction: prev.type === type && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
    setMenuVisible(false)
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={menuContainerRef}>
      <SecondaryButton
        icon={<Filter width={12} />}
        style={{ borderColor: 'rgba(138, 148, 166, 1)' }}
        onClick={() => setMenuVisible(!menuVisible)}
      >
        {`Ordenar por ${sortConfig.type === 'date' ? 'fecha' : 'visualizaciones'} ${
          sortConfig.direction === 'asc' ? '↑' : '↓'
        }`}
      </SecondaryButton>

      {menuVisible && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            zIndex: 1000,
            boxShadow:
              '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
            backgroundColor: '#fff',
            borderRadius: '2px',
            minWidth: '220px',
            marginTop: '4px',
            border: '1px solid #f0f0f0',
          }}
        >
          <Menu
            selectedKeys={[]}
            style={{ boxShadow: 'none', border: 'none' }}
            onSelect={({ key }) => handleMenuClick(key as SortType)}
            items={[
              {
                key: 'date',
                label: (
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <span>Ordenar por fecha</span>
                    {sortConfig.type === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </div>
                ),
              },
              {
                key: 'impressions',
                label: (
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <span>Ordenar por visualizaciones</span>
                    {sortConfig.type === 'impressions' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  )
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigateToContent }) => {
  const { userInfo } = useAuthContext();
  const { performanceMetrics, currentPage, setCurrentPage, displayedMetrics } = useDashboardContext()

  return (
    <Space
      direction='vertical'
      size='large'
      style={{
        width: '100%',
        padding: '16px',
        paddingLeft: '0px',
      }}
    >
      <Row justify='space-between' align='middle'>
        <Col>
          <Title style={{ fontWeight: 500 }}>
            Hola, <span style={{ fontWeight: 700 }}>{userInfo?.['custom:clientId']}</span>
          </Title>
        </Col>
        <Col>
          <Button
            style={{
              width: '200px',
              height: '45px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 1)',
            }}
            icon={<Plus width={15} />}
            onClick={onNavigateToContent}
          >
            Cargar Contenido
          </Button>
        </Col>
      </Row>

      <DashboardNavbar />
      <Row justify='center'>
        <Col lg={16}>
          <MetricsContent data={performanceMetrics.summary} />
        </Col>
      </Row>

      <Row justify='end'>
        <Col>
          <SortButton />
        </Col>
      </Row>

      <Row gutter={16} justify={'center'}>
        <Col lg={16} style={{ marginBottom: '20px' }}>
          <ConfigProvider>
            <AnimatePresence mode='sync'>
              {displayedMetrics.videos &&
                displayedMetrics.videos.map((data, index) => (
                  <motion.div
                    key={data.video_source}
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1, // efecto de cascada
                    }}
                  >
                    <MetricsContent data={data} />
                  </motion.div>
                ))}
            </AnimatePresence>
          </ConfigProvider>

          <Pagination
            current={currentPage}
            total={performanceMetrics.videos.length}
            pageSize={ITEMS_PER_PAGE}
            onChange={setCurrentPage}
            style={{ display: 'flex', justifyContent: 'center' }}
          />
        </Col>
      </Row>
    </Space>
  )
}
export default DashboardPage
